import type { ComponentType, ReactNode, Ref } from "react";
import {
  SymAblauf,
  SymAufgabe,
  SymInfoWinzig,
  SymPerson,
  SymPersonAus,
  SymSeite,
  SymServer,
  SymVerboten,
  SymZeitung,
  type SymGroesse,
} from "./lese-symbole";
import {
  VERWEIS_GRUNDSICHT_ART_MARKE,
  VERWEIS_MODULWORT,
  VERWEIS_NICHT_VERFUEGBAR_MARKE,
  VERWEIS_NICHT_VERFUEGBAR_TEXT,
  type VerweisArt,
} from "./lese-verweis-art";

/*
 * Der Verweis-Chip des Lesepfads (Auftrag „Verweis-Chips Optik A“, Betreiber 14.10.2026: „Chips A, nicht verfügbar ja“,
 * Design-Guide `docs/ui-preline/designguide/verweis-chip.md`, Muster M-089). Zwei Segmente: links das Lucide-Symbol und das
 * Modulwort in gedämpftem Ton, rechts der Name. Das Modul steht ohne Hover und ohne Farbe im Chip (Handy, Barrierefreiheit).
 * Drei Zustände mit gleicher Form: normal, „Nicht verfügbar“ (gestrichelt, ohne Namen; gelöscht und kein Recht sind
 * nicht unterscheidbar, r95) und ausgeschiedene Person (Name bleibt, Chip leise). Grundlage ist der Katalogblock
 * `ui-components/…/ui-components-cat-badge-styles/badge-with-divider-segment`; nur Theme-Tokens, keine eigenen Klassen.
 */

/** Anker für die Hovercard (Delegation am Container im Editor); gleiche Form wie `LeseAnker` in den Ansichten. */
export interface VerweisAnker {
  "data-hover-art": "person" | "wiki" | "ci" | "workflow";
  "data-hover-id": string;
  tabIndex: 0;
}

/** Symbol je Verweis-Art (Lucide); das Modulwort kommt aus der einen Quelle `VERWEIS_MODULWORT`. */
export const VERWEIS_SYMBOL: Record<VerweisArt, ComponentType<SymGroesse>> = {
  cmdb: SymServer,
  workflow: SymAblauf,
  person: SymPerson,
  wiki: SymSeite,
  intranet: SymZeitung,
  aufgabe: SymAufgabe,
  prozess: SymAblauf,
};

/** Art der Hover-Karte je Verweis-Art (Intranet hat noch keine Karte). */
const HOVER_ART: Partial<Record<VerweisArt, VerweisAnker["data-hover-art"]>> = {
  cmdb: "ci",
  workflow: "workflow",
  person: "person",
  wiki: "wiki",
};

/**
 * `grundsicht` (Name und Grundangaben ohne Vollrecht) und `gattung` (nur das Wort der Art) sehen gleich aus (Betreiber W191 A,
 * 14.10.2026): gleiche zwei Segmente, ganzer Chip gedämpft, kleines Info-Symbol hinter dem Namen, kein Link, kein Verbotssymbol.
 */
export type VerweisChipZustand = "normal" | "nicht-verfuegbar" | "ausgeschieden" | "grundsicht" | "gattung";

const RAHMEN_NORMAL =
  "inline-flex items-center align-middle bg-layer border border-layer-line text-layer-foreground text-xs rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus";
const RAHMEN_LEISE =
  "inline-flex items-center align-middle bg-surface border border-line-2 text-muted-foreground-1 text-xs rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus";
const RAHMEN_GESTRICHELT =
  "inline-flex items-center align-middle bg-surface border border-dashed border-line-2 text-muted-foreground-1 text-xs rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus";

const KENNZEICHEN_NORMAL = "py-1 px-2 inline-flex items-center gap-1.5 text-muted-foreground-1";
const KENNZEICHEN_LEISE = "py-1 px-2 inline-flex items-center gap-1.5";
const NAME_NORMAL = "py-1 px-2 border-s border-layer-line font-medium text-sm rounded-e-md";
const NAME_LEISE = "py-1 px-2 border-s border-line-2 text-sm rounded-e-md";
const NAME_GESTRICHELT = "py-1 px-2 border-s border-dashed border-line-2 text-sm rounded-e-md";
const ENTFERNEN_SEGMENT =
  "py-1 px-2 inline-flex items-center border-s border-layer-line text-muted-foreground-1 hover:text-foreground rounded-e-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none";
const NAME_GRUNDSICHT = "py-1 px-2 border-s border-line-2 text-sm rounded-e-md inline-flex items-center gap-1.5";

export function VerweisChip({
  art,
  zustand = "normal",
  anker = null,
  href,
  neuesFenster = false,
  wort,
  symbol,
  onEntfernen,
  entfernenLabel,
  entfernenRef,
  belegt = false,
  children,
}: {
  /**
   * Wort im linken Segment statt des Modulworts der Art (Besprechung-Nachzug 19.10.2026, AW-121 (6)): für Ziele, deren Wort nicht aus `VERWEIS_MODULWORT`
   * kommt („Aufgabe“, „Risiko“, „Dokument“, „Besprechung“, „Konfigurationselement“ der Agenda-Bezüge). Additiv; ohne Angabe wie bisher.
   */
  wort?: string;
  /** Symbol (vom Aufrufer, `aria-hidden`) statt des Symbols der Art; passend zu `wort`. */
  symbol?: ReactNode;
  /**
   * Entfernen-Aktion (✕ als drittes Segment, nur mit Schreibrecht, Anbieten ≤ Erlauben): der Chip fragt nicht selbst zurück, die Rückfrage („Bezug entfernen?“)
   * steht beim Aufrufer unter dem Chip (`ZeilenRueckfrage`). Ohne `onEntfernen` kein ✕ und die Form bleibt die bisherige.
   */
  onEntfernen?: () => void;
  /** Zugänglicher Name des ✕ („Bezug entfernen: Titel“); Pflicht mit `onEntfernen`. */
  entfernenLabel?: string;
  /** Ref auf das ✕ (Fokus-Rückkehr nach Abbrechen). */
  entfernenRef?: Ref<HTMLButtonElement>;
  belegt?: boolean;
  art: VerweisArt;
  zustand?: VerweisChipZustand;
  /** Anker der Karte; bei „nicht verfügbar“ setzt der Chip selbst einen mit der Marke (der Lader beantwortet sie ohne Zugriff). */
  anker?: VerweisAnker | null;
  /** Nur gesetzt, wenn der Chip ein Link ist (Wiki-Verweis in der Link-Marke); bei „nicht verfügbar“ nie. */
  href?: string;
  neuesFenster?: boolean;
  /** Der Name; bei „nicht verfügbar“ ohne Inhalt steht der feste Text. */
  children?: ReactNode;
}) {
  const eingeschraenkt = zustand === "grundsicht" || zustand === "gattung";
  const Zeichen = zustand === "ausgeschieden" ? SymPersonAus : zustand === "nicht-verfuegbar" ? SymVerboten : VERWEIS_SYMBOL[art];
  const hoverArt = HOVER_ART[art];
  // „Nicht verfügbar“ und die Gattung tragen eine Marke statt einer Kennung (die Karte beantwortet sie ohne Aufruf); eine
  // Grundsicht behält den Anker des Aufrufers (Kennung für die Karte mit Name und Typ, ohne Link).
  const traeger: VerweisAnker | null =
    zustand === "nicht-verfuegbar" && hoverArt
      ? { "data-hover-art": hoverArt, "data-hover-id": VERWEIS_NICHT_VERFUEGBAR_MARKE, tabIndex: 0 }
      : zustand === "gattung" && hoverArt
        ? { "data-hover-art": hoverArt, "data-hover-id": VERWEIS_GRUNDSICHT_ART_MARKE, tabIndex: 0 }
        : anker;
  const rahmen =
    zustand === "normal" ? RAHMEN_NORMAL : zustand === "ausgeschieden" || eingeschraenkt ? RAHMEN_LEISE : RAHMEN_GESTRICHELT;
  const kennzeichen = zustand === "normal" ? KENNZEICHEN_NORMAL : KENNZEICHEN_LEISE;
  const namenSegment =
    zustand === "normal"
      ? NAME_NORMAL
      : zustand === "ausgeschieden"
        ? NAME_LEISE
        : eingeschraenkt
          ? NAME_GRUNDSICHT
          : NAME_GESTRICHELT;
  const inhalt = (
    <>
      <span className={kennzeichen}>
        {symbol ?? <Zeichen klein />}
        {wort ?? VERWEIS_MODULWORT[art]}
      </span>
      <span className={namenSegment}>
        {zustand === "nicht-verfuegbar" ? (children ?? VERWEIS_NICHT_VERFUEGBAR_TEXT) : children}
        {zustand === "ausgeschieden" ? <span className="text-xs"> (ausgeschieden)</span> : null}
        {eingeschraenkt ? <SymInfoWinzig /> : null}
      </span>
    </>
  );
  if (onEntfernen) {
    // Mit ✕: drei Segmente in EINEM Rahmen; das ✕ ist ein eigener Knopf (kein Knopf im Link). Anker und Link wandern auf die ersten zwei Segmente.
    const innen = "inline-flex items-center focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus rounded-s-md";
    return (
      <>
        {/* Preline: ui-components/ui-components-sub-actions-status-progress/ui-components-cat-badge-styles/badge-with-divider-segment (drittes Segment ✕, Besprechung-Nachzug, AW-121 (6)) */}
        <span className={rahmen} aria-busy={belegt || undefined}>
          {href && zustand !== "nicht-verfuegbar" && !eingeschraenkt ? (
            <a href={href} target={neuesFenster ? "_blank" : undefined} rel={neuesFenster ? "noopener noreferrer" : undefined} {...(traeger ?? {})} className={innen}>
              {inhalt}
            </a>
          ) : (
            <span {...(traeger ?? {})} className={innen}>
              {inhalt}
            </span>
          )}
          <button
            ref={entfernenRef}
            type="button"
            className={ENTFERNEN_SEGMENT}
            aria-label={entfernenLabel ?? "Entfernen"}
            title="Entfernen"
            disabled={belegt}
            onClick={onEntfernen}
          >
            <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </span>
      </>
    );
  }
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-actions-status-progress/ui-components-cat-badge-styles/badge-with-divider-segment */}
      {href &&zustand !== "nicht-verfuegbar" && !eingeschraenkt ? (
        <a
          href={href}
          target={neuesFenster ? "_blank" : undefined}
          rel={neuesFenster ? "noopener noreferrer" : undefined}
          {...(traeger ?? {})}
          className={rahmen}
        >
          {inhalt}
        </a>
      ) : (
        <span {...(traeger ?? {})} className={rahmen}>
          {inhalt}
        </span>
      )}
    </>
  );
}
